// thunks.ts
import { createAsyncThunk } from "@reduxjs/toolkit"
import { EntityWithId } from "@/core/factory/types"
import { createEntityEndpoints } from "@/core/factory/endpoints"
import {
  STATUS_LOADING,
  STATUS_LOADED,
  STATUS_SAVING,
  STATUS_SAVED,
  STATUS_LOAD_ERROR,
  STATUS_SAVE_ERROR
} from "@/redux/_status.types"

async function withStatus<T>(
  dispatch: any,
  actions: any,
  fn: () => Promise<T>,
  {
    startStatus,
    successStatus,
    errorStatus,
    errorMessage,
    onSuccess
  }: {
    startStatus: string
    errorStatus: string
    successStatus: string
    errorMessage: string
    onSuccess?: (res: any) => void
  }
) {
  try {
    dispatch(actions.setStatus(startStatus))
    const res: any = await fn()
    if (res?.error) throw new Error(res.error)

    if (onSuccess) onSuccess(res)

    dispatch(actions.setStatus(successStatus))
    return res
  } catch (err: any) {
    dispatch(actions.setStatus(errorStatus))
    dispatch(actions.setError(err.message || errorMessage))
    throw err
  }
}

export function createEntityThunks<T extends EntityWithId>(
  entityName: string,
  endpoint: ReturnType<typeof createEntityEndpoints>,
  actions: any
) {
  const fetchAndSet = createAsyncThunk<T[], string | undefined>(
    `${entityName}/fetch-and-set`,
    async (route, { dispatch }) =>
      withStatus(dispatch, actions, () => endpoint.fetch(route), {
        startStatus: STATUS_LOADING,
        successStatus: STATUS_LOADED,
        errorStatus: STATUS_LOAD_ERROR,
        errorMessage: "Fetch failed",
        onSuccess: (res) => dispatch(actions.setList(res))
      })
  )

  const createEntity = createAsyncThunk<T, T>(`${entityName}/create`, async (entity, { dispatch }) =>
    withStatus(dispatch, actions, () => endpoint.create(entity), {
      startStatus: STATUS_SAVING,
      successStatus: STATUS_SAVED,
      errorStatus: STATUS_SAVE_ERROR,
      errorMessage: "Create failed",
      onSuccess: (res) => {
        dispatch(actions.replace(res))
        dispatch(actions.setActive(res))
      }
    })
  )

  const updateEntity = createAsyncThunk<T, void>(`${entityName}/update`, async (_, { dispatch, getState }) =>
    withStatus(
      dispatch,
      actions,
      () => {
        const state: any = getState()
        const draft: T = state[entityName].draft
        return endpoint.update(draft)
      },
      {
        startStatus: STATUS_SAVING,
        successStatus: STATUS_SAVED,
        errorStatus: STATUS_SAVE_ERROR,
        errorMessage: "Update failed",
        onSuccess: () => {
          dispatch(actions.sync())
        }
      }
    )
  )

  const removeEntity = createAsyncThunk<string, string>(`${entityName}/remove`, async (id, { dispatch }) =>
    withStatus(dispatch, actions, () => endpoint.remove(id), {
      startStatus: STATUS_SAVING,
      successStatus: STATUS_SAVED,
      errorStatus: STATUS_SAVE_ERROR,
      errorMessage: "Remove failed",
      onSuccess: () => dispatch(actions.remove(id))
    })
  )

  return { fetchAndSet, createEntity, updateEntity, removeEntity }
}
